import { DATE_FORMATTING_OPTIONS } from '../../../../chartRenderer/dateFormattingOptions';
import { supportsStackValues } from '../../utils/chartStacking';
import s from './AxisConfigurator.module.scss';

const CATEGORY_ORDERING_OPTIONS = [
    { value: 'asc', label: 'Ascending' },
    { value: 'desc', label: 'Descending' },
    { value: 'none', label: 'None (original order)' },
];

const SORTING_TYPE_OPTIONS = [
    { value: 'number', label: 'Number' },
    { value: 'string', label: 'String' },
    { value: 'date', label: 'Date' },
];

const DECIMAL_PLACES_CHART_TYPES = [
    'line',
    'area',
    'column',
    'bar',
    'polar',
    'mixed',
    'heatmap',
];

const AxisConfigurator = ({
    yAxis,
    xAxis,
    chartType,
    decimalPlaces,
    onYAxisChange,
    onXAxisChange,
    onDecimalPlacesChange,
    validationErrors = {},
}) => {
    const showStackOption = supportsStackValues(chartType);
    const isHeatmap = chartType === 'heatmap';
    const supportsDecimalPlaces =
        chartType && DECIMAL_PLACES_CHART_TYPES.includes(chartType);

    const handleYAxisFieldChange = (field, value) => {
        onYAxisChange({ ...yAxis, [field]: value });
    };

    const handleXAxisFieldChange = (field, value) => {
        onXAxisChange({ ...xAxis, [field]: value });
    };

    const parseNumberInput = (value) => {
        if (value === '' || value === null) return null;
        const num = parseFloat(value);
        return isNaN(num) ? null : num;
    };

    const parseDecimalPlacesInput = (value) => {
        if (value === '' || value === null) return null;
        const n = parseInt(value, 10);
        if (Number.isNaN(n) || n < 0) return null;
        return n;
    };

    return (
        <div className={s.Root}>
            <div className={s.Section}>
                <h3 className={s.SectionTitle}>X-Axis</h3>
                <div className={s.Fields}>
                    <div className={s.Field}>
                        <label className={s.Label}>Label</label>
                        <input
                            type="text"
                            className={s.Input}
                            value={xAxis.label || ''}
                            onChange={(e) =>
                                handleXAxisFieldChange('label', e.target.value)
                            }
                            placeholder="X-axis label"
                        />
                    </div>

                    <div className={s.Field}>
                        <label className={s.Label}>Category Ordering</label>
                        <select
                            className={s.Select}
                            value={xAxis.categoryOrdering || 'asc'}
                            onChange={(e) =>
                                handleXAxisFieldChange(
                                    'categoryOrdering',
                                    e.target.value
                                )
                            }>
                            {CATEGORY_ORDERING_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={s.Field}>
                        <label className={s.Label}>Sorting Type</label>
                        <select
                            className={s.Select}
                            value={xAxis.categorySortingType || 'number'}
                            onChange={(e) =>
                                handleXAxisFieldChange(
                                    'categorySortingType',
                                    e.target.value
                                )
                            }>
                            {SORTING_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={s.Field}>
                        <label className={s.Label}>Label Formatting</label>
                        <select
                            className={s.Select}
                            value={xAxis.labelFormatting || 'none'}
                            onChange={(e) =>
                                handleXAxisFieldChange(
                                    'labelFormatting',
                                    e.target.value
                                )
                            }>
                            {DATE_FORMATTING_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className={s.Section}>
                <h3 className={s.SectionTitle}>Y-Axis</h3>
                <div className={s.Fields}>
                    <div className={s.Field}>
                        <label className={s.Label}>Label</label>
                        <input
                            type="text"
                            className={s.Input}
                            value={yAxis.label || ''}
                            onChange={(e) =>
                                handleYAxisFieldChange('label', e.target.value)
                            }
                            placeholder="Y-axis label"
                        />
                    </div>

                    {supportsDecimalPlaces && isHeatmap && (
                        <div className={s.Field}>
                            <label className={s.Label}>Decimal places</label>
                            <input
                                type="number"
                                className={s.Input}
                                min={0}
                                step={1}
                                value={decimalPlaces ?? ''}
                                onChange={(e) =>
                                    onDecimalPlacesChange(
                                        parseDecimalPlacesInput(e.target.value)
                                    )
                                }
                                placeholder="Auto"
                            />
                        </div>
                    )}

                    {!isHeatmap && (
                        <>
                            <div className={s.Row}>
                                <div className={s.Field}>
                                    <label className={s.Label}>Min</label>
                                    <input
                                        type="number"
                                        className={`${s.Input} ${validationErrors['yAxis.min'] ? s.Invalid : ''}`}
                                        value={yAxis.min ?? ''}
                                        onChange={(e) =>
                                            handleYAxisFieldChange(
                                                'min',
                                                parseNumberInput(e.target.value)
                                            )
                                        }
                                        placeholder="Auto"
                                    />
                                </div>
                                <div className={s.Field}>
                                    <label className={s.Label}>Max</label>
                                    <input
                                        type="number"
                                        className={`${s.Input} ${validationErrors['yAxis.max'] ? s.Invalid : ''}`}
                                        value={yAxis.max ?? ''}
                                        onChange={(e) =>
                                            handleYAxisFieldChange(
                                                'max',
                                                parseNumberInput(e.target.value)
                                            )
                                        }
                                        placeholder="Auto"
                                    />
                                </div>
                            </div>

                            {validationErrors['yAxis.minMax'] && (
                                <div className={s.ErrorMessage}>
                                    {validationErrors['yAxis.minMax']}
                                </div>
                            )}

                            <div className={s.Field}>
                                <label className={s.Label}>Tick Interval</label>
                                <input
                                    type="number"
                                    className={s.Input}
                                    value={yAxis.tickInterval ?? ''}
                                    onChange={(e) =>
                                        handleYAxisFieldChange(
                                            'tickInterval',
                                            parseNumberInput(e.target.value)
                                        )
                                    }
                                    placeholder="Auto"
                                />
                            </div>

                            {supportsDecimalPlaces && (
                                <div className={s.Field}>
                                    <label className={s.Label}>
                                        Decimal places
                                    </label>
                                    <input
                                        type="number"
                                        className={s.Input}
                                        min={0}
                                        step={1}
                                        value={decimalPlaces ?? ''}
                                        onChange={(e) =>
                                            onDecimalPlacesChange(
                                                parseDecimalPlacesInput(
                                                    e.target.value
                                                )
                                            )
                                        }
                                        placeholder="Auto"
                                    />
                                </div>
                            )}
                        </>
                    )}

                    {showStackOption && (
                        <div className={s.CheckboxField}>
                            <label className={s.CheckboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={yAxis.stackValues || false}
                                    onChange={(e) =>
                                        handleYAxisFieldChange(
                                            'stackValues',
                                            e.target.checked
                                        )
                                    }
                                />
                                <span>Stack values</span>
                            </label>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AxisConfigurator;
